import React, { useState } from 'react';
import applicationService from '../services/applicationService';

const ClearanceSection = ({ application, userRole, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [clearanceFile, setClearanceFile] = useState(null);

  const isHrAdmin = ['HR', 'ADMIN', 'CHAIRMAN'].includes(userRole);
  const isCandidate = userRole === 'CANDIDATE';

  const clearance = application?.clearance || {};

  const getDocumentViewUrl = (url, fileName = '') => {
    if (!url) return url;

    const lowerName = (fileName || '').toLowerCase();
    const lowerUrl = String(url).toLowerCase();
    const isPreviewableDoc =
      lowerName.endsWith('.pdf') ||
      lowerName.endsWith('.doc') ||
      lowerName.endsWith('.docx') ||
      lowerUrl.includes('.pdf') ||
      lowerUrl.includes('.doc') ||
      lowerUrl.includes('.docx');

    if (isPreviewableDoc) {
      return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`;
    }

    return url;
  };

  const withAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
      if (onRefresh) onRefresh();
    } catch (error) {
      alert(error.response?.data?.error || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleApproveClearance = () => {
    withAction(async () => {
      await applicationService.approveClearance(application._id);
      alert('Clearance approved successfully. You can now upload clearance document.');
    });
  };

  const handleUploadClearance = () => {
    if (!clearanceFile) {
      alert('Please select a clearance file first');
      return;
    }

    withAction(async () => {
      await applicationService.uploadClearanceDocument(application._id, clearanceFile);
      alert('Clearance document uploaded successfully.');
      setClearanceFile(null);
      const input = document.getElementById('clearance-upload-input');
      if (input) input.value = '';
    });
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">Clearance</h3>

      <div className="flex flex-col gap-3">
        {clearance.approvedAt ? (
          <div className="text-sm text-[#155724] bg-[#d4edda] border border-[#c3e6cb] rounded p-2">
            Clearance cleared
          </div>
        ) : (
          <div className="text-sm text-[#6c757d] bg-[#f8f9fa] border border-[#dee2e6] rounded p-2">
            {isCandidate ? 'Waiting for admin to approve clearance.' : 'Clearance has not been approved yet.'}
          </div>
        )}

        {isHrAdmin && !clearance.approvedAt && (
          <button
            type="button"
            disabled={busy}
            onClick={handleApproveClearance}
            className="px-4 py-2.5 border-none rounded-md text-sm font-semibold cursor-pointer transition-all duration-200 bg-[#007bff] text-white hover:bg-[#0056b3] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? 'Processing...' : 'Approve Clearance'}
          </button>
        )}

        {clearance.documentFileUrl && (
          <div className="flex flex-col gap-1">
            <a
              href={getDocumentViewUrl(clearance.documentFileUrl, clearance.documentFileName)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white max-w-fit"
            >
              View Clearance Document
            </a>
            <span className="text-xs text-[#6c757d]">{clearance.documentFileName || 'clearance-document.pdf'}</span>
            {isCandidate && (
              <span className="text-xs text-[#6c757d]">Uploaded by admin.</span>
            )}
          </div>
        )}

        {isHrAdmin && clearance.approvedAt && !clearance.documentFileUrl && (
          <div className="bg-[#f8f9fa] border border-[#dee2e6] rounded p-3">
            <label htmlFor="clearance-upload-input" className="block text-sm font-medium text-[#495057] mb-2">
              Upload Clearance Document
            </label>
            <input
              id="clearance-upload-input"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              disabled={busy}
              onChange={(e) => setClearanceFile(e.target.files?.[0] || null)}
              className="w-full p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white mb-2"
            />
            <button
              type="button"
              disabled={busy || !clearanceFile}
              onClick={handleUploadClearance}
              className="px-4 py-2.5 border-none rounded-md text-sm font-semibold cursor-pointer transition-all duration-200 bg-accent-green text-white hover:bg-[#5a6b4d] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? 'Processing...' : 'Upload Clearance Document'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClearanceSection;

